/// <reference path="../../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />

import Dispatcher from '../dispatcher';
import { TranscribingActionTypes, DeleteStoredTranscriptionAndRouteToLanding } from './TranscribingActions';
import { TranscriptionRoutePathNames } from '../routes';
import { TaskListResultStatus, TaskList } from '../../backend/TaskList';
import { LogEvents } from '../../utils/LogEvents';
import { StartOverKeys } from '../pages/telemetry/StartOverKeys';
import { TranscriptionSourceFileErrors, TranscriptionSourceFileError } from '../../backend/TranscriptionManagerErrors';
import { IForegroundTask } from '../../backend/interfaces/IForegroundTask';
import { HostAppSettings, HostAppSettingName } from '../../backend/office-js/HostAppSettings';
import { ITranscriptionSourceFile, ITranscriptionSourceFileProvider } from '../../backend/interfaces/ITranscriptionSourceFileProvider';
import { CreateTranscriptionSourceFileProvider } from '../../backend/TranscriptionSourceFileFactory';
import { IAuthenticationProvider } from '../../backend/interfaces/IAuthenticationProvider';
import { GetStoredTranscription } from '../../backend/office-js/DocumentStorage';

let loadTranscriptionSourceFileTaskList: TaskList<ITranscriptionSourceFile> | null = null;

export const LoadingActionTypes = {
  SetForegroundTaskAction: "LOADING_SET_FOREGROUND_TASK",
  SetStatusAction: "LOADING_SET_STATUS",
  RevertStateAction: "LOADING_REVERT_STATE",
  SetPercentDoneAction: "LOADING_SET_PERCENT_DONE",
  SetShowAuthPopupAction: "LOADING_SET_SHOW_AUTH_POPUP"
};

export enum LoadingStatus {
  LoadingTranscription,
  UploadingFile,
  TranscribingAudio
}

export function SetShowAuthPopup(showAuthPopup: boolean) {
  Dispatcher.dispatch({
    type: LoadingActionTypes.SetShowAuthPopupAction,
    payload: showAuthPopup
  });
}

export function SetForegroundTaskInStore(foregroundTask: IForegroundTask) {
  Dispatcher.dispatch({
    type: LoadingActionTypes.SetForegroundTaskAction,
    payload: foregroundTask
  });
}

export async function LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage(transcriptionSourceFileProvider: ITranscriptionSourceFileProvider, history: any) {
  loadTranscriptionSourceFileTaskList = new TaskList<ITranscriptionSourceFile | null>([
    async () => {
      try {
        const sourceFile = await transcriptionSourceFileProvider.GetSourceFile();
        return sourceFile;
      } catch (error) {
        if (error instanceof TranscriptionSourceFileError && error.name == TranscriptionSourceFileErrors.SourceFileNotFound) {
          // If the source file couldn't be found, return null so Transcribing page is loaded without an audio file.
          return null;
        }
        throw error;
      }
    } 
  ]);

  const result = await loadTranscriptionSourceFileTaskList.ExecuteTaskListSequentially();
  loadTranscriptionSourceFileTaskList = null;

  if (result.status == TaskListResultStatus.Canceled)
    return;

  Dispatcher.dispatch({
    type: TranscribingActionTypes.SetSourceFileAction,
    payload: {
      sourceFile: result.result
    }
  });

  history.replace(TranscriptionRoutePathNames.Transcribing);
}

// GetTranscriptionFromExistingMedia is used to get transcription data for a previously triggered transcription
export async function GetTranscriptionFromExistingMedia(authProvider: IAuthenticationProvider, history: any) {
  if (HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {
    Dispatcher.dispatch({
      type: LoadingActionTypes.SetStatusAction,
      payload: LoadingStatus.LoadingTranscription
    });

    const transcriptionSourceFileProvider: ITranscriptionSourceFileProvider = CreateTranscriptionSourceFileProvider(GetStoredTranscription(), authProvider);
    await LoadTranscriptionSourceFileToStoreAndNavigateToTranscribingPage(transcriptionSourceFileProvider, history);
  }
}

export async function CancelClicked(history: any, foregroundTask?: IForegroundTask) {
  LogEvents.StartOver.Start([{ key: StartOverKeys.Reason, value: "Cancel" }]);
  if (foregroundTask != null)
    await foregroundTask.Cancel();

  if (loadTranscriptionSourceFileTaskList != null) {
    loadTranscriptionSourceFileTaskList.Cancel();
    loadTranscriptionSourceFileTaskList = null;
  }

  DeleteStoredTranscriptionAndRouteToLanding(history);
  LogEvents.ServiceLiveSpeechToText.AddResponse({ isSuccessful: true, message: "Cancelled by user", isFinalResponse: true });
  LogEvents.StartOver.End();
}

export function StatusLabelFromLoadingStatus(status: LoadingStatus): string {
  switch (status) {
    case LoadingStatus.UploadingFile:
      return TranscriptionStrings.LoadingPageUploadingStatus;
    case LoadingStatus.TranscribingAudio:
      return TranscriptionStrings.LoadingPageTranscribingStatus;
    case LoadingStatus.LoadingTranscription:
      return TranscriptionStrings.LoadingPageFetchingStatus;
  }
}

export function ProgressLabelFromLoadingStatus(status: LoadingStatus): string {
  const blankString = '';
  switch (status) {
    case LoadingStatus.UploadingFile:
    case LoadingStatus.TranscribingAudio:
      return TranscriptionStrings.LoadingPageTakingLongLabel;

    default:
      return blankString;
  }
}